import { ApiError } from "@hakili/api-client";
import { t } from "@hakili/i18n";
import { BANK_TEXT_2, Callout, Field, GuardedButton, Input, classes } from "@hakili/ui";
import { CircleAlert, Info, TriangleAlert } from "lucide-react";
import { useEffect, useId, useState } from "react";

import {
  InertPrimary,
  LOGIN_FORM,
  LOGIN_SUB,
  LOGIN_TITLE,
  LoginShell,
  QuietLink,
} from "./connexion-panel.js";

/**
 * `RESET-PASSWORD`, R2 (D-1024-4, D-1024-10; frames 104 to 108 and 115 of `design/bank-v2` at `10c95f31`): **the page a reset
 * link lands on, `/nouveau-mot-de-passe#{token}`.**
 *
 * The token is in the URL FRAGMENT so that no access log, proxy or `Referer` ever holds it (`password-reset-path.ts`, Q4). The
 * app reads it ONCE, strips it from the address bar and hands it in; from here it travels only in the BODY of a `POST`.
 * The screen's first act is to ask the door what the link is (`reset-check`): `valid` shows the form, `expired` (an unknown
 * or superseded link reads the same) and `used` show their own page, so a banker is never given a form that cannot save.
 *
 * **Two guards, each with its reason on the screen** (I19): under twelve characters, then two entries that differ. The door
 * repeats both. Its other refusal is the policy's (`06 §6.1.1`): the frame draws « Ce mot de passe est trop courant. » and
 * the screen shows any other policy sentence in the door's own words. Saving closes every session (the door's act, R1's) and
 * the screen hands over to the sign-in, which says so (frame 108).
 */

export interface LienLu {
  readonly state: "valid" | "expired" | "used";
  readonly email?: string | undefined;
}

const MIN_LENGTH = 12;

type Read =
  | { readonly kind: "reading" }
  | { readonly kind: "valid"; readonly email: string | null }
  | { readonly kind: "expired" }
  | { readonly kind: "used" };

function DeadLink(props: {
  readonly state: "expired" | "used";
  readonly onNewLink: () => void;
  readonly onBack: () => void;
}): React.JSX.Element {
  const expired = props.state === "expired";
  return (
    <LoginShell>
      <h1
        className={LOGIN_TITLE}
        data-testid={expired ? "bank-reinit-expire-titre" : "bank-reinit-servi-titre"}
      >
        {expired ? t("bank.reinit.expireTitre") : t("bank.reinit.serviTitre")}
      </h1>
      <div className={LOGIN_FORM}>
        <Callout
          tone={expired ? "warn" : "info"}
          icon={
            expired ? (
              <TriangleAlert className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Info className="h-4 w-4" aria-hidden="true" />
            )
          }
          testId={expired ? "bank-reinit-expire" : "bank-reinit-servi"}
        >
          {expired ? (
            <>
              <strong>{t("bank.reinit.expire")}</strong> {t("bank.reinit.expireSecurite")}
            </>
          ) : (
            <>
              <strong>{t("bank.reinit.servi")}</strong> {t("bank.reinit.serviSuite")}
            </>
          )}
        </Callout>
        {/* The wrapper keeps the button at its natural width, like « Enregistrer le mot de passe » (designer's review, change 2). */}
        <div>
          {expired ? (
            <GuardedButton variant="primary" onClick={props.onNewLink} testId="bank-reinit-nouveau-lien">
              {t("bank.oubli.nouveauLien")}
            </GuardedButton>
          ) : (
            <GuardedButton variant="primary" onClick={props.onBack} testId="bank-reinit-connexion">
              {t("bank.login.entrer")}
            </GuardedButton>
          )}
        </div>
        {expired ? (
          <QuietLink onClick={props.onBack} testId="bank-reinit-retour">
            {t("bank.oubli.retour")}
          </QuietLink>
        ) : (
          <QuietLink onClick={props.onNewLink} testId="bank-reinit-nouveau-lien">
            {t("bank.oubli.nouveauLien")}
          </QuietLink>
        )}
      </div>
    </LoginShell>
  );
}

export function NouveauMotDePasse(props: {
  /** What the link carried, read once by the app; `null` for a link with no (or a malformed) token. */
  readonly token: string | null;
  readonly check: (token: string) => Promise<LienLu>;
  readonly reset: (token: string, password: string) => Promise<void>;
  readonly onDone: () => void;
  readonly onNewLink: () => void;
  readonly onBack: () => void;
}): React.JSX.Element {
  const { token, check } = props;
  const [read, setRead] = useState<Read>(token === null ? { kind: "expired" } : { kind: "reading" });
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const differId = useId();

  useEffect(() => {
    if (token === null) return;
    let live = true;
    check(token)
      .then((lu) => {
        if (live)
          setRead(lu.state === "valid" ? { kind: "valid", email: lu.email ?? null } : { kind: lu.state });
      })
      .catch(() => {
        // A link that cannot be read is a link that cannot be used: the page says so in the frame's words.
        if (live) setRead({ kind: "expired" });
      });
    return () => {
      live = false;
    };
  }, [token, check]);

  if (read.kind === "reading") {
    return (
      <LoginShell>
        <p className={classes("text-sm", BANK_TEXT_2)} data-testid="bank-reinit-charge">
          {t("bank.session.chargement")}
        </p>
      </LoginShell>
    );
  }
  if (read.kind === "expired" || read.kind === "used") {
    return <DeadLink state={read.kind} onNewLink={props.onNewLink} onBack={props.onBack} />;
  }

  const tooShort = password.length < MIN_LENGTH;
  const differ = again !== "" && password !== again;
  // The 12-character guard stands beside the button (no field says it). The mismatch is said ONCE: under the second field once it is
  // typed (designer's review of `95ef8568`, change 3), beside the button only while that field is still empty.
  const reason = tooShort ? t("bank.reinit.garde12") : again === "" ? t("bank.reinit.gardeIdentiques") : "";
  const mismatchOnly = reason === "" && password !== again;

  const submit = (event: Pick<Event, "preventDefault">): void => {
    event.preventDefault();
    if (reason !== "" || mismatchOnly || pending || token === null) return;
    setPending(true);
    setRefusal(null);
    props
      .reset(token, password)
      .then(() => {
        props.onDone();
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 410) {
          setRead({ kind: cause.message === t("bank.reinit.porteServi") ? "used" : "expired" });
          return;
        }
        if (cause instanceof ApiError && cause.message.startsWith(t("bank.reinit.tropCourant"))) {
          setRefusal(`${t("bank.reinit.tropCourant")} ${t("bank.reinit.tropCourantSuite")}`);
          return;
        }
        setRefusal(
          cause instanceof ApiError && cause.message !== "" ? cause.message : t("common.error.inattendue"),
        );
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <LoginShell>
      <h1 className={LOGIN_TITLE} data-testid="bank-reinit-titre">
        {t("bank.reinit.titre")}
      </h1>
      <p className={LOGIN_SUB} data-testid="bank-reinit-consigne">
        {t("bank.reinit.consigneAvant")}
        {read.email ?? t("bank.reinit.consigneVotreCompte")}.
      </p>
      <form
        className={LOGIN_FORM}
        onSubmit={submit}
        data-testid="bank-reinit"
        aria-label={t("bank.reinit.titre")}
        noValidate
      >
        <Field
          label={t("bank.reinit.nouveau")}
          htmlFor="bank-reinit-nouveau"
          hint={<span data-testid="bank-reinit-aide">{t("bank.reinit.aide")}</span>}
        >
          <Input
            id="bank-reinit-nouveau"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
            testId="bank-reinit-nouveau"
          />
        </Field>
        <Field
          label={t("bank.reinit.confirmer")}
          htmlFor="bank-reinit-confirmer"
          error={
            differ ? (
              <span id={differId} data-testid="bank-reinit-differents">
                {t("bank.reinit.differents")}
              </span>
            ) : undefined
          }
        >
          <Input
            id="bank-reinit-confirmer"
            type="password"
            value={again}
            onChange={setAgain}
            autoComplete="new-password"
            invalid={differ}
            required
            testId="bank-reinit-confirmer"
          />
        </Field>
        {refusal === null ? null : (
          <Callout
            tone="bad"
            icon={<CircleAlert className="h-4 w-4" aria-hidden="true" />}
            testId="bank-reinit-refus"
          >
            {refusal}
          </Callout>
        )}
        <div>
          {mismatchOnly ? (
            <InertPrimary describedBy={differId} testId="bank-reinit-enregistrer">
              {t("bank.reinit.enregistrer")}
            </InertPrimary>
          ) : reason === "" ? (
            <GuardedButton type="submit" variant="primary" testId="bank-reinit-enregistrer">
              {t("bank.reinit.enregistrer")}
            </GuardedButton>
          ) : (
            <GuardedButton disabled type="submit" reason={reason} testId="bank-reinit-enregistrer">
              {t("bank.reinit.enregistrer")}
            </GuardedButton>
          )}
        </div>
      </form>
    </LoginShell>
  );
}
